import { queryAccountSitesWithoutBranded } from '@wix/ambassador-mobile-v1-branded-app/http'
import { Box, Heading, Image, SelectorList, SelectorListDatasourceFn, SelectorListItem } from '@wix/design-system'
import { FreeTextQuestionContentComponent, useContent, useNext, usePageLoading } from '@wix/funnel-builder-lizard'
import React, { FC, useEffect, useState } from 'react'
import { useMediaQuery } from 'react-responsive'

import { ambassadorHttpClient } from '../../../api/httpClient'
import { usePageLogger } from '../../../bi/mfi'
import { LARGE_DESKTOP } from '../../../breakpoints'
import { BackButton } from '../../reusable/BottomLeftBackButton'
import { BrandedAppLogoWithBack } from '../../reusable/BrandedAppLogoWithBack'
import { Button } from '../../reusable/Button'
import { SkipButton } from '../../reusable/SkipButton'
import { VisualBox } from '../../reusable/VisualBox/VisualBox'
import s from './BrandedAppSiteSelctor.scss'

export type TBrandedAppSiteSelector = {
  back: string
  skip: string
  next: string
  title: string
  skipPosition: string
  placeholder: string
  image: string
  hint: string
  suggestionsLabel: string
  backgroundColor: string
  question: FreeTextQuestionContentComponent<{}>
}

export type Site = {
  id: string
  title: string
  logo: string
  metaSiteId: string
  viewerUrl: string
  siteDisplayName: string
}
export const BrandedAppSiteSelctor: FC = () => {
  const [clickedButton, setClickedButton] = useState<'next' | 'back' | 'skip'>()
  const { content } = useContent<TBrandedAppSiteSelector>()
  const { skip, back, skipPosition, next: nextBtn, question, title, image, backgroundColor } = content
  const { questionKey } = question
  const [pageLoading] = usePageLoading()

  const [selectedSite, setSelectedSite] = useState<Site>({
    id: '',
    title: '',
    logo: '',
    metaSiteId: '',
    viewerUrl: '',
    siteDisplayName: '',
  })

  const next = useNext()

  const pageLogger = usePageLogger()

  const isLargeDesktopScreen = useMediaQuery(LARGE_DESKTOP)

  const handleChange = (value: Site) => {
    void pageLogger.clickAnswer({ questionKey, buttonName: String(value) })
    setSelectedSite(value)
  }

  const handleSkip = () => {
    if (clickedButton !== undefined || pageLoading) {
      return
    }
    setClickedButton('skip')
    void pageLogger.generalAction({ action: 'skip' })
    next({
      [questionKey]: {
        skipped: true,
      },
    })
  }

  const handleBack = () => {
    if (clickedButton !== undefined) {
      return
    }
    setClickedButton('back')
    void pageLogger.generalAction({ action: 'back' })
    window.history.back()
  }
  const handleNext = async () => {
    if (clickedButton !== undefined) {
      return
    }
    setClickedButton('next')
    void pageLogger.answer({ answer: selectedSite?.id || '' })
    next({
      [questionKey]: { values: [selectedSite?.id ?? ''] },
    })
  }

  useEffect(() => {
    void pageLogger.screenView()
  }, [pageLogger])

  const fetchSiteList = async (): Promise<SelectorListItem[]> => {
    const res = await ambassadorHttpClient.request(queryAccountSitesWithoutBranded({}))
    const parsedData = res?.data?.sites?.map(site => ({
      id: site.metaSiteId || '',
      title: site.siteDisplayName || '',
      subtitle: (site.viewerUrl || 'Not Published').replace(/^https:\/\//, ''),
      image: (
        <Image
          showBorder
          height={60}
          width={102}
          fit="cover"
          src={`https://www.wix.com/site-thumbnail/${site.metaSiteId}`}
        />
      ),
    }))
    return parsedData || []
  }

  const getDataSource: SelectorListDatasourceFn = async (searchQuery, offset, limit) => {
    try {
      const items = await fetchSiteList()
      const filteredItems = items.filter(({ title }) => `${title}`.toLowerCase().includes(searchQuery.toLowerCase()))
      return {
        items: filteredItems.slice(offset, offset + limit),
        totalCount: filteredItems.length,
      }
    } catch (error) {
      console.error('Error fetching data:', error)
      return {
        items: [],
        totalCount: 0,
      }
    }
  }
  const mainContentNode = (
    <Box direction="vertical" verticalAlign="middle" className={s.container}>
      <Box className={s.titleContainer}>
        <Heading className={s.title}>{title}</Heading>
      </Box>

      <div style={{ height: '400px', width: '578px' }}>
        <SelectorList
          size="medium"
          dataSource={getDataSource}
          onSelect={e => {
            handleChange(e as Site)
          }}
        />
      </div>

      <Box marginTop="42px">
        <Button
          isLoading={clickedButton === 'next'}
          onClick={handleNext}
          label={nextBtn}
          disabled={selectedSite.id === ''}
          size={isLargeDesktopScreen ? 'large' : 'medium'}
          className={!selectedSite ? s.hidden : undefined}
        />
      </Box>
    </Box>
  )

  const imageNode = (
    <Box position="relative" width="100%" backgroundColor={backgroundColor} align="center" verticalAlign="middle">
      <Box className={`${s.videoWrapper} ${s.visible}`}>
        <VisualBox visualType="image" src={image} light />
      </Box>
    </Box>
  )

  return (
    <Box height="100vh" overflow="hidden">
      <BrandedAppLogoWithBack />
      <Box width="55%" minWidth="calc(456px + 90px + 90px)" height="100%" overflow="hidden">
        <Box align="center" className="fade-in-bottom">
          {mainContentNode}
        </Box>
      </Box>
      <Box flex="1" minWidth="608px" className="fade-in">
        {imageNode}
      </Box>
      {back && <BackButton type="TextButton" onClick={handleBack} backButton={back} />}
      {skip && (
        <>
          <SkipButton skin="dark" skipButton={skip} position={skipPosition} onClick={handleSkip} />
        </>
      )}
    </Box>
  )
}
